import { useEffect, useState } from 'react';
import { Admin } from '../../api.js';
import { PageHeader } from '../../components/PageHeader.jsx';
import { FormField } from '../../components/FormField.jsx';
import { ConfirmDialog } from '../../components/Modal.jsx';

export default function StripeSettings() {
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ publishableKey: '', secretKey: '', webhookSecret: '' });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [err, setErr] = useState(null);
  const [toast, setToast] = useState(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const [copied, setCopied] = useState(false);
  const [autoSetup, setAutoSetup] = useState(null);
  const [autoBusy, setAutoBusy] = useState(false);

  async function refresh() {
    setLoading(true);
    try {
      const r = await Admin.settings.stripe.get();
      setCurrent(r);
      setForm({
        publishableKey: r.publishableKey || '',
        secretKey: '',
        webhookSecret: '',
      });
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { refresh(); }, []);

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    setErr(null);
    setTestResult(null);
    try {
      const payload = {};
      if (form.publishableKey !== (current?.publishableKey || '')) payload.publishableKey = form.publishableKey || undefined;
      if (form.secretKey) payload.secretKey = form.secretKey;
      if (form.webhookSecret) payload.webhookSecret = form.webhookSecret;
      if (Object.keys(payload).length === 0) {
        setToast('Nothing to save — all fields unchanged.');
        setTimeout(() => setToast(null), 3000);
        setSaving(false);
        return;
      }
      await Admin.settings.stripe.save(payload);
      setToast('Stripe keys saved.');
      setTimeout(() => setToast(null), 3000);
      await refresh();
    } catch (e) {
      setErr(e.data?.issues ? e.data.issues.map((i) => i.message).join(', ') : e.message);
    } finally {
      setSaving(false);
    }
  }

  async function test() {
    setTesting(true);
    setTestResult(null);
    setErr(null);
    try {
      const r = await Admin.settings.stripe.test(form.secretKey || undefined);
      setTestResult(r);
    } catch (e) {
      setTestResult({ ok: false, error: e.data?.error || 'error', message: e.data?.message || e.message });
    } finally {
      setTesting(false);
    }
  }

  async function autoSetupWebhook() {
    setAutoBusy(true);
    setAutoSetup(null);
    try {
      const r = await Admin.settings.stripe.autoSetupWebhook(form.secretKey || undefined);
      setAutoSetup({ ok: true, ...r });
      setToast('Webhook registered and signing secret saved.');
      setTimeout(() => setToast(null), 4000);
      await refresh();
    } catch (e) {
      setAutoSetup({ ok: false, message: e.data?.message || e.message });
    } finally {
      setAutoBusy(false);
    }
  }

  async function clear() {
    await Admin.settings.stripe.clear();
    setToast('Stripe configuration cleared.');
    setTimeout(() => setToast(null), 3000);
    await refresh();
  }

  function copyWebhookUrl() {
    if (!current?.webhookEndpoint) return;
    navigator.clipboard?.writeText(current.webhookEndpoint);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  if (loading) return <div className="p-8 text-ink-3">Loading…</div>;

  const sourceLabel = {
    settings: 'Using keys from this settings page',
    env: 'Using fallback keys from environment variables',
    none: 'Not configured',
  }[current?.source] || current?.source;

  return (
    <div>
      <PageHeader
        eyebrow="Integrations"
        title="Stripe"
        description="Connect your Stripe account to collect payments for rentals and products. Paste your keys below — they're stored server-side and never exposed to the browser."
      />

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-ink text-canvas px-4 py-2.5 rounded-full text-sm shadow-modal z-50">
          {toast}
        </div>
      )}

      {/* Status summary */}
      <div className={`card mb-6 ${current?.source === 'settings' ? 'border-success/30 bg-success/[0.04]' : current?.source === 'none' ? 'border-warn/30 bg-warn/[0.04]' : ''}`}>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="eyebrow mb-1">Status</div>
            <div className="font-display text-lg">{sourceLabel}</div>
            {current?.secretKeyRedacted && (
              <p className="text-sm text-ink-2 mt-2">
                Secret: <code className="font-mono text-xs">{current.secretKeyRedacted}</code>
                {current.webhookSecretRedacted && <> · Webhook: <code className="font-mono text-xs">{current.webhookSecretRedacted}</code></>}
                {current.updatedAt && <> · Updated {new Date(current.updatedAt).toLocaleString()}</>}
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={test} className="btn-outline btn-sm" disabled={testing}>
              {testing ? 'Testing…' : 'Test connection'}
            </button>
            {current?.source === 'settings' && (
              <button onClick={() => setConfirmClear(true)} className="btn-ghost btn-sm text-danger">
                Clear keys
              </button>
            )}
          </div>
        </div>
        {testResult && (
          <div className={`mt-4 p-3 rounded-md border ${testResult.ok ? 'border-success/30 bg-success/[0.06]' : 'border-danger/30 bg-danger/[0.06]'}`}>
            {testResult.ok ? (
              <div className="text-sm">
                <span className="chip-success mr-2">{testResult.mode}</span>
                {testResult.keyType === 'restricted' && <span className="chip-brand mr-2">restricted</span>}
                {testResult.limitedVisibility ? (
                  <>Connection works — <em>account details hidden</em> (no <code className="text-xs">Account: read</code> scope, which is fine).</>
                ) : (
                  <>
                    Connected to <strong>{testResult.businessName || testResult.accountId}</strong>
                    {testResult.email && <> · {testResult.email}</>}
                    {' — '}
                    Charges {testResult.chargesEnabled ? 'enabled' : 'disabled'}, Payouts {testResult.payoutsEnabled ? 'enabled' : 'disabled'}
                  </>
                )}
              </div>
            ) : (
              <div className="text-sm text-danger">{testResult.message || testResult.error}</div>
            )}
          </div>
        )}
      </div>

      {/* Restricted-key guidance */}
      <div className="card mb-6 border-brand/30 bg-brand/[0.04]">
        <div className="eyebrow text-brand mb-2">Recommended</div>
        <h2 className="font-display text-xl mb-2">Use a restricted API key, not your full secret</h2>
        <p className="text-sm text-ink leading-relaxed mb-4">
          Stripe secret keys (<code className="text-xs">sk_live_...</code>) have full powers — charges, refunds, payouts, customer data. A <strong>restricted key</strong> (<code className="text-xs">rk_live_...</code>) limits what this app can do if the key is ever leaked. Takes ~2 minutes to create.
        </p>
        <ol className="text-sm text-ink-2 list-decimal pl-5 space-y-1.5 mb-4">
          <li>Open <a href="https://dashboard.stripe.com/apikeys/create" target="_blank" rel="noreferrer" className="text-brand underline underline-offset-4">Stripe Dashboard → Developers → API keys → Create restricted key</a>.</li>
          <li>Name it something like <em>"Blaze admin"</em>.</li>
          <li>Grant only these permissions (everything else: <strong>None</strong>):</li>
        </ol>
        <div className="grid sm:grid-cols-2 gap-2 mb-4">
          {[
            ['Checkout Sessions', 'Write'],
            ['Payment Links', 'Write'],
            ['Webhook Endpoints', 'Write (lets us register the webhook for you)'],
            ['Customers', 'Read'],
            ['Products', 'Read'],
            ['Prices', 'Read'],
            ['Payment Intents', 'Read'],
            ['Charges', 'Read'],
            ['Account', 'Read (optional — richer status display)'],
          ].map(([resource, perm]) => (
            <div key={resource} className="flex items-center gap-2 text-sm bg-paper rounded border border-line px-3 py-2">
              <span className="chip-neutral text-[10px] shrink-0">{perm}</span>
              <span>{resource}</span>
            </div>
          ))}
        </div>
        <p className="text-xs text-ink-3">
          Click <strong>Create key</strong>, then copy the generated <code className="text-[10px]">rk_live_...</code> value into the <em>Secret key</em> field below. You can revoke or rotate a restricted key at any time without affecting any other Stripe integration.
        </p>
      </div>

      <form onSubmit={save} className="card space-y-5 mb-6">
        <h2 className="font-display text-xl mb-2">API keys</h2>
        <p className="text-sm text-ink-2 -mt-2">
          Paste your publishable key and either a restricted key (<code className="text-xs">rk_...</code>, recommended) or full secret key (<code className="text-xs">sk_...</code>).
        </p>

        <FormField
          label="Publishable key"
          hint="Safe to share. Starts with pk_test_ (test mode) or pk_live_ (live)."
        >
          <input
            className="input font-mono text-xs"
            value={form.publishableKey}
            onChange={(e) => setForm({ ...form, publishableKey: e.target.value })}
            placeholder="pk_live_..."
            autoComplete="off"
          />
        </FormField>

        <FormField
          label="Secret / restricted key"
          hint={
            current?.secretKeyRedacted
              ? `Currently set to ${current.secretKeyRedacted}. Leave blank to keep the existing key.`
              : 'rk_live_... (recommended) or sk_live_... — stored server-side only.'
          }
        >
          <input
            type="password"
            className="input font-mono text-xs"
            value={form.secretKey}
            onChange={(e) => setForm({ ...form, secretKey: e.target.value })}
            placeholder={current?.secretKeyRedacted ? '(unchanged)' : 'rk_live_...'}
            autoComplete="off"
          />
        </FormField>

        <div className="pt-4 border-t border-line">
          <h3 className="font-display text-lg mb-1">Webhook</h3>
          <p className="text-sm text-ink-2 mb-4">
            Stripe sends us events when payments complete so we can confirm bookings automatically. If your key has the <code className="text-xs">Webhook Endpoints: Write</code> scope, we can register the endpoint and save the signing secret for you in one click.
          </p>

          <div className="card bg-canvas/60 border-line mb-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="eyebrow mb-1">Automatic setup</div>
                <p className="text-sm text-ink-2">
                  {current?.webhookSecretRedacted
                    ? <>Webhook already configured (<code className="text-xs">{current.webhookSecretRedacted}</code>).</>
                    : 'Click to register a webhook endpoint in your Stripe account and store the signing secret here.'}
                </p>
              </div>
              <button
                type="button"
                onClick={autoSetupWebhook}
                className="btn-primary btn-sm"
                disabled={autoBusy}
              >
                {autoBusy ? 'Setting up…' : current?.webhookSecretRedacted ? 'Re-register webhook' : 'Set up webhook automatically'}
              </button>
            </div>
            {autoSetup && (
              <div className={`mt-3 p-3 rounded-md border text-sm ${autoSetup.ok ? 'border-success/30 bg-success/[0.06]' : 'border-danger/30 bg-danger/[0.06]'}`}>
                {autoSetup.ok ? (
                  <>
                    <strong>Registered.</strong> Listening for {autoSetup.events?.length || 4} event types.
                  </>
                ) : (
                  <span className="text-danger">{autoSetup.message}</span>
                )}
              </div>
            )}
          </div>

          <details>
            <summary className="text-xs text-ink-3 cursor-pointer hover:text-ink">
              Manual webhook setup (skip if automatic worked)
            </summary>
            <div className="mt-3 pl-3 border-l-2 border-line space-y-3">
              <p className="text-xs text-ink-3">
                In Stripe → Developers → Webhooks, add an endpoint at the URL below. Subscribe to <code className="text-[10px]">checkout.session.completed</code>, <code className="text-[10px]">checkout.session.async_payment_succeeded</code>, <code className="text-[10px]">checkout.session.expired</code>, <code className="text-[10px]">charge.refunded</code>. Copy the signing secret Stripe shows you.
              </p>
              <FormField label="Webhook endpoint URL">
                <div className="flex gap-2">
                  <input className="input font-mono text-xs" value={current?.webhookEndpoint || ''} readOnly />
                  <button type="button" onClick={copyWebhookUrl} className="btn-outline btn-sm whitespace-nowrap">
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                </div>
              </FormField>
              <FormField
                label="Webhook signing secret"
                hint={
                  current?.webhookSecretRedacted
                    ? `Currently set to ${current.webhookSecretRedacted}. Leave blank to keep.`
                    : 'Starts with whsec_.'
                }
              >
                <input
                  type="password"
                  className="input font-mono text-xs"
                  value={form.webhookSecret}
                  onChange={(e) => setForm({ ...form, webhookSecret: e.target.value })}
                  placeholder={current?.webhookSecretRedacted ? '(unchanged)' : 'whsec_...'}
                  autoComplete="off"
                />
              </FormField>
            </div>
          </details>
        </div>

        {err && (
          <div className="px-3 py-2 rounded-md bg-danger/10 text-danger text-sm">{err}</div>
        )}

        <div className="flex items-center gap-3 pt-2 border-t border-line">
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save keys'}
          </button>
          <button type="button" onClick={test} className="btn-outline" disabled={testing}>
            {testing ? 'Testing…' : 'Test'}
          </button>
        </div>
      </form>

      <div className="text-xs text-ink-3 space-y-1">
        <p>· Keys are stored in Firestore and never exposed to the browser.</p>
        <p>· All Stripe API calls made by the server will use these keys.</p>
        <p>· If keys are cleared, the server falls back to the <code>STRIPE_*</code> environment variables (if set).</p>
      </div>

      <ConfirmDialog
        open={confirmClear}
        onClose={() => setConfirmClear(false)}
        onConfirm={clear}
        title="Clear Stripe keys?"
        message="Payments and payment links will stop working until you configure new keys (or until the server falls back to env vars, if set)."
        confirmLabel="Clear"
        danger
      />
    </div>
  );
}
